import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box, Link } from '@mui/material'
import { makeStyles } from '@mui/styles'
import * as R from 'ramda'

import { RxPrescriptionOrigin } from '~/api/graphql/generated/types'
import usePrescriptionEditor from '~/components/dashboard/prescription/prescription-editor/hooks/usePrescriptionEditor'
import PrescriptionBody from '~/components/dashboard/prescription/prescription-editor/PrescriptionBody'
import PrescriptionHeader from '~/components/dashboard/prescription/prescription-editor/PrescriptionHeader'
import { PrescriptionWorkflowType } from '~/constants/PrescriptionWorkflow'
import {
  getIsFetchingChewyItems,
  getPrescriptionChewyItems,
} from '~/store/duck/prescriptions'
import type { CRUDOrderOptions, Order, Prescription } from '~/types'

const RETURN_LINK_FONT_SIZE = '1.2rem'
const RETURN_LINK_FONT_WEIGHT = 500

const useStyles = makeStyles((theme) => ({
  container: {
    position: 'relative',
  },
  returnLinkContainer: {
    position: 'absolute',
    // Position after Drug Info section (approximate height using theme spacing)
    // Drug Info section is roughly 22 spacing units tall
    top: theme.spacing(22),
    right: theme.spacing(2),
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 10,
  },
  returnLink: {
    cursor: 'pointer',
    fontSize: RETURN_LINK_FONT_SIZE,
    fontWeight: RETURN_LINK_FONT_WEIGHT,
  },
  patientLinkContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(-3),
  },
}))

export interface EditPrescriptionStepHandle {
  getHeader: () => React.ReactNode
  getPrescription: () => Prescription
  validate: () => boolean
}

interface EditPrescriptionStepProps {
  clientId: string
  onCancel?: () => void
  onProceed?: () => void
  onReturnToPatientSelection?: () => void
  onReturnToProductSearch?: () => void
  patientId: string
  prescription: Prescription
  showReturnToPatient?: boolean
  showReturnToProduct?: boolean
}

export const EditPrescriptionStep = forwardRef<
  EditPrescriptionStepHandle,
  EditPrescriptionStepProps
>(function EditPrescriptionStep(
  {
    clientId,
    patientId,
    prescription,
    onProceed,
    onCancel,
    onReturnToProductSearch,
    onReturnToPatientSelection,
    showReturnToPatient,
    showReturnToProduct,
  },
  ref,
) {
  const { t } = useTranslation('Dialogs')
  const classes = useStyles()
  const chewyItems = useSelector(getPrescriptionChewyItems)
  const isFetchingChewyItems = useSelector(getIsFetchingChewyItems)

  // Transform to Vet-initiated to display as Active Rx (shows Chewy Items, autoship, etc.)
  const editablePrescription = useMemo(
    () =>
      ({
        ...R.omit(['stateId'], prescription),
        id: prescription.id,
        origin: RxPrescriptionOrigin.Vet,
      }) as Prescription,
    [prescription],
  )

  const {
    validate,
    refs,
    fields,
    prescription: currentPrescription,
    activeWorkflow,
    handleSetActiveWorkflow,
    ...prescriptionBodyProps
  } = usePrescriptionEditor({
    clientId,
    patientId,
    prescription: editablePrescription,
    areChargesPostedAndEditable: true, // Lock workflow to Active Rx
    disableWorkflowSelector: true, // Disable workflow radio buttons - locked to Chewy Active Rx
    outsideSoap: true,
    skipOrderFetching: true, // Skip fetching - search result doesn't have complete data
    onClose: () => {
      onCancel?.()
    },
    onSave: (_updated: Order, _options: CRUDOrderOptions) => {
      onProceed?.()
    },
  })

  // Force Chewy Active Rx workflow for swap product flow (shows as "chewy.com" in dropdown)
  useEffect(() => {
    if (
      activeWorkflow &&
      activeWorkflow !== PrescriptionWorkflowType.CHEWY_ACTIVE_RX
    ) {
      handleSetActiveWorkflow(PrescriptionWorkflowType.CHEWY_ACTIVE_RX)
    }
  }, [activeWorkflow])

  const hasChewyItems = chewyItems && chewyItems.length > 0
  // Only show return link if Chewy items are loaded and available
  const shouldShowReturnToProduct =
    showReturnToProduct && hasChewyItems && !isFetchingChewyItems

  const getPrescriptionData = useCallback((): Prescription => {
    const administrationData = refs.administrationSectionRef.current?.get()
    const quantityData = refs.quantitySectionRef.current?.get()
    const refillableData = refs.refillableSectionRef.current?.get()
    const taskData = refs.taskSectionRef.current?.get()
    const dispensedFromData = refs.dispensedFromSectionRef.current?.get()

    return {
      ...currentPrescription,
      ...administrationData,
      ...quantityData,
      ...refillableData,
      ...dispensedFromData,
      doctorId: fields.doctorId.value,
      // Save selected Chewy item
      partNumber: fields.chewyItem.value,
      // Save autoship settings only if enabled
      retailOrderLineItem: {
        ...currentPrescription.retailOrderLineItem,
        autoshipFrequency: fields.enableAutoship?.value
          ? fields.autoshipFrequency?.value
          : null,
        autoshipUnitId: fields.enableAutoship?.value
          ? fields.autoshipUnitId?.value
          : null,
      },
      ...(fields.automaticallyCreateTask.value && taskData
        ? {
            clientTasks: {
              dueDateTime: taskData.dueDate,
              name: taskData.name,
              recurrenceEndDateTime: taskData.recurrenceEndDatetime,
              recurrenceOffset: taskData.recurrencePeriod
                ? {
                    amount: taskData.recurrencePeriod.amount,
                    unitId: taskData.recurrencePeriod.unit,
                  }
                : undefined,
            },
          }
        : {}),
    } as Prescription
  }, [currentPrescription, refs, fields])

  const renderHeader = useCallback(
    () => (
      <Box>
        <PrescriptionHeader
          clientId={clientId}
          patientId={patientId}
          prescription={editablePrescription}
        />
        {showReturnToPatient && onReturnToPatientSelection && (
          <Box className={classes.patientLinkContainer}>
            <Link
              className={classes.returnLink}
              component="button"
              underline="none"
              onClick={onReturnToPatientSelection}
            >
              {t('SWAP_PRODUCT_DIALOG.RETURN_TO_CHANGE_PATIENT')}
            </Link>
          </Box>
        )}
      </Box>
    ),
    [
      clientId,
      patientId,
      editablePrescription,
      showReturnToPatient,
      onReturnToPatientSelection,
      classes,
      t,
    ],
  )

  // Expose methods to parent via ref
  useImperativeHandle(
    ref,
    () => ({
      getHeader: renderHeader,
      getPrescription: getPrescriptionData,
      validate,
    }),
    [renderHeader, getPrescriptionData, validate],
  )

  return (
    <Box className={classes.container}>
      {shouldShowReturnToProduct && onReturnToProductSearch && (
        <Box className={classes.returnLinkContainer}>
          <Link
            className={classes.returnLink}
            component="button"
            underline="none"
            onClick={onReturnToProductSearch}
          >
            {t('SWAP_PRODUCT_DIALOG.RETURN_TO_PRODUCT_SEARCH')}
          </Link>
        </Box>
      )}
      <PrescriptionBody
        {...prescriptionBodyProps}
        activeWorkflow={activeWorkflow}
        fields={fields}
        handleSetActiveWorkflow={handleSetActiveWorkflow}
        prescription={currentPrescription}
        refs={refs}
        validate={validate}
      />
    </Box>
  )
})
